/**
 * Component: ReferencePopover
 * SpecReferenceID: BROWSE-V1
 * Requirements: BROWSE-V1-R08
 *
 * Description: Floating citation-reference popover bound to the
 *              inline anchors inside rendered sectionText. The
 *              parent (SectionContent) tracks a single active
 *              reference at a time and passes `{anchor, reference}`
 *              when the user clicks an anchor; the popover positions
 *              itself next to the anchor using its bounding client
 *              rect and traps focus until dismissed.
 *
 *              The component is intentionally NOT using
 *              @radix-ui/react-popover because the inline citation
 *              anchors are produced by SafeHtml via
 *              dangerouslySetInnerHTML, which can't be wrapped in a
 *              Radix `<PopoverTrigger>`. A controlled positioned
 *              panel with click-outside + Escape keeps the surface
 *              small and avoids the virtual-anchor workarounds Radix
 *              would require.
 *
 *              Keyboard: Escape closes; focus returns to the
 *              originating anchor on dismiss. The panel itself is
 *              focused on mount so a screen-reader user hears the
 *              reference contents immediately.
 *
 * Risk Links: R4
 * FMEA Links: FM2
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { SafeHtml } from '@/components/SafeHtml';
import type { ReferenceEntry } from '../types';

interface ReferencePopoverProps {
  anchor: HTMLAnchorElement;
  reference: ReferenceEntry;
  onClose: () => void;
}

export function ReferencePopover({
  anchor,
  reference,
  onClose,
}: ReferencePopoverProps): React.ReactElement {
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Position the panel relative to the anchor. We use `fixed` so
  // the panel isn't constrained by any ancestor `overflow:hidden`,
  // and we compute `top/left` from the anchor's bounding client
  // rect at mount. The panel intentionally doesn't track scroll
  // events — clicking an anchor opens a fresh panel, and clicking
  // or scrolling dismisses it via the outside-click handler.
  const rect = anchor.getBoundingClientRect();
  const style: React.CSSProperties = {
    position: 'fixed',
    top: `${rect.bottom + 8}px`,
    left: `${rect.left}px`,
    maxWidth: 'min(24rem, calc(100vw - 2rem))',
  };

  // Focus the panel on mount and return focus to the anchor on unmount.
  useEffect(() => {
    panelRef.current?.focus();
    return () => {
      try {
        anchor.focus({ preventScroll: true });
      } catch {
        /* anchor may have been detached; ignore */
      }
    };
  }, [anchor]);

  // Escape to close.
  useEffect(() => {
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Click outside to close.
  useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      const panel = panelRef.current;
      const target = event.target as Node | null;
      if (!panel || !target) return;
      if (panel.contains(target)) return;
      if (anchor.contains(target)) return;
      onClose();
    };
    // Defer one frame so the originating click doesn't immediately
    // fire this handler on the document.
    const handle = requestAnimationFrame(() => {
      document.addEventListener('mousedown', handleClick);
    });
    return () => {
      cancelAnimationFrame(handle);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [anchor, onClose]);

  const handleCloseClick = useCallback(() => onClose(), [onClose]);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label={`Reference ${reference.referenceId}`}
      tabIndex={-1}
      style={style}
      data-testid={`reference-popover-${reference.referenceId}`}
      className="z-50 rounded-md border border-border bg-popover p-4 text-sm text-popover-foreground shadow-lg outline-none focus:ring-2 focus:ring-primary"
    >
      <div className="mb-2 flex items-start justify-between gap-4">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Reference {reference.referenceId}
        </span>
        <button
          type="button"
          onClick={handleCloseClick}
          aria-label="Close reference"
          data-testid={`reference-popover-${reference.referenceId}-close`}
          className="rounded p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {reference.html ? (
        <SafeHtml
          html={reference.html}
          className="prose prose-sm max-w-none text-foreground"
        />
      ) : (
        <p className="whitespace-pre-wrap text-foreground">{reference.referenceText}</p>
      )}
      {reference.url && (
        <p className="mt-3">
          <a
            href={reference.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary"
          >
            View source
          </a>
        </p>
      )}
    </div>
  );
}
